import { useEffect, useMemo, useState } from "react";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import app from "./firebase";
import styles from "./IngredientPurchaseHistoryDashboard.module.css";
import { format2 } from "./utils/format2";

const db = getFirestore(app);

const SORT_FIELDS = [
  { key: "date", label: "Date" },
  { key: "ingredientName", label: "Ingredient" },
  { key: "quantity", label: "Quantity" },
  { key: "totalQuantity", label: "Total Quantity" },
  { key: "costPerBaseUnit", label: "Cost / Base Unit" },
  { key: "totalPrice", label: "Total Price" },
  { key: "pricePerLb", label: "Price / lb" },
  { key: "storeName", label: "Store" }
];

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  // Firestore Timestamp
  if (typeof value === "object" && typeof value.seconds === "number") {
    return new Date(value.seconds * 1000);
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatDate = (value) => {
  const date = toJsDate(value);
  return date ? date.toLocaleDateString() : "-";
};

const compareValues = (a, b) => {
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;

  // Dates
  const dateA = toJsDate(a);
  const dateB = toJsDate(b);
  if (dateA && dateB) return dateA.getTime() - dateB.getTime();

  // Numbers
  const numA = typeof a === "number" ? a : Number(a);
  const numB = typeof b === "number" ? b : Number(b);
  const numAValid = Number.isFinite(numA);
  const numBValid = Number.isFinite(numB);
  if (numAValid && numBValid) return numA - numB;

  // Strings
  return String(a).localeCompare(String(b));
};

export default function IngredientPurchaseHistoryDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [storeFilter, setStoreFilter] = useState("all");
  const [sortField, setSortField] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "ingredient_purchase_history"));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setRows(data);
      } catch (err) {
        console.error("Error fetching ingredient purchase history:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const storeOptions = useMemo(() => {
    const unique = new Map();
    rows.forEach(r => {
      const id = r.storeId || "unknown";
      const name = r.storeName || "Unknown";
      if (!unique.has(id)) unique.set(id, name);
    });
    return Array.from(unique.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filteredAndSorted = useMemo(() => {
    const term = search.trim().toLowerCase();

    const filtered = rows.filter(r => {
      if (storeFilter !== "all") {
        const id = r.storeId || "unknown";
        if (id !== storeFilter) return false;
      }

      if (!term) return true;
      const haystack = [r.ingredientName, r.storeName]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });

    const dir = sortDir === "asc" ? 1 : -1;
    return filtered.sort((a, b) => dir * compareValues(a?.[sortField], b?.[sortField]));
  }, [rows, search, storeFilter, sortField, sortDir]);

  if (loading) {
    return <p className={styles.loading}>Loading purchase history...</p>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Ingredient Purchase History</h2>
      </div>

      <div className={styles.controls}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Search ingredient or store..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className={styles.select}
          value={storeFilter}
          onChange={(e) => setStoreFilter(e.target.value)}
        >
          <option value="all">All Stores</option>
          {storeOptions.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        <select
          className={styles.select}
          value={sortField}
          onChange={(e) => setSortField(e.target.value)}
        >
          {SORT_FIELDS.map(f => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>

        <select
          className={styles.select}
          value={sortDir}
          onChange={(e) => setSortDir(e.target.value)}
        >
          <option value="desc">Desc</option>
          <option value="asc">Asc</option>
        </select>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Ingredient</th>
              <th>Quantity (Base Unit)</th>
              <th>Total Quantity</th>
              <th>Cost / Base Unit</th>
              <th>Total Price</th>
              <th>Price / lb</th>
              <th>Store</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.map(r => {
              const unit = r.unit || "";
              const quantity = r.quantity ?? "-";
              const totalQty = r.totalQuantity ?? "-";
              return (
                <tr key={r.id}>
                  <td>{formatDate(r.date || r.createdAt)}</td>
                  <td>{r.ingredientName || "-"}</td>
                  <td>{quantity === "-" ? "-" : `${format2(quantity)}${unit ? ` ${unit}` : ""}`}</td>
                  <td>{totalQty === "-" ? "-" : `${format2(totalQty)}${unit ? ` ${unit}` : ""}`}</td>
                  <td>{r.costPerBaseUnit === null || r.costPerBaseUnit === undefined ? "-" : format2(r.costPerBaseUnit)}</td>
                  <td>{r.totalPrice === null || r.totalPrice === undefined ? "-" : format2(r.totalPrice)}</td>
                  <td>{r.pricePerLb === null || r.pricePerLb === undefined ? "-" : format2(r.pricePerLb)}</td>
                  <td>{r.storeName || "-"}</td>
                </tr>
              );
            })}
            {filteredAndSorted.length === 0 && (
              <tr>
                <td className={styles.empty} colSpan={8}>No purchase history found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
